import { faCamera } from '@fortawesome/pro-solid-svg-icons/faCamera';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useClientAccountOverview } from '@ukpostbox/client-data';

import {
	AvatarSize,
	Button,
	Colour,
	ColourStateSolid,
	ContentLoader,
	FlexAlign,
	FlexGap,
	FlexJustify,
	Pill,
	StackHorizontal,
	StackVertical,
	Tooltip,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { useWindowScrollPositions } from '@ukpostbox/frontend-framework/lib/Hooks/useWindowScrollPosition';
import { FormatCurrency } from '@ukpostbox/helpers';
import React from 'react';
import { faCog } from '@fortawesome/pro-solid-svg-icons/faCog';
import { useUser } from '../../User/Providers/UserProvider';
import { EndpointError } from '../../_Global/Components/EndpointError';
import { UserAvatar } from '../../User/Components/UserAvatar';
import { UserMenuDropdownMenu } from '../../_Global/Components/UserMenu';

type AccountHeaderType = {
	inOverview?: boolean;
};

function AccountHeader(props: AccountHeaderType) {
	const { t } = useTranslation();
	const windowPosition = useWindowScrollPositions();
	const { SelectNewAvatar } = useUser();
	const { isTiny, isSmall, isMedium, isLarge, isXL, isXXL, isUltraWide } = useLayout();
	const { data, status, error } = useClientAccountOverview();
	const { data: userData, status: userStatus, error: userError } = useClientAccountOverview();

	return (
		<>
			{status === 'error' && <EndpointError {...error} />}

			{status === 'pending' && (
				<StackHorizontal extraClass='pt-sm pb-sm mb-xl'>
					<ContentLoader
						round
						childCount={1}
						childWidth={isLarge ? '6em' : '4.5em'}
						childHeight={isLarge ? '6em' : '4.5em'}
					/>

					<div className='flex-column justify-center ml-sm mr-a flex-gap-xs'>
						<ContentLoader childHeight='2.4em' childCount={1} childWidth='12em' />
						<ContentLoader childHeight='1.5em' childCount={1} childWidth='19em' />
					</div>
				</StackHorizontal>
			)}

			{status !== 'pending' && status !== 'error' && userStatus !== 'error' && (
				<div className={`user-account-header mb-xl ${windowPosition.scrollY !== 0 ? 'scrolling' : ''}`}>
					<div
						className='user-avatar-wrapper'
						onClick={() => SelectNewAvatar()}
						onKeyDown={(e) => (e.key === 'Enter' ? SelectNewAvatar() : null)}
						role='button'
						tabIndex={0}
					>
						<div className='user-avatar-icon'>
							<FontAwesomeIcon icon={faCamera} size='2x' />
						</div>
						<UserAvatar size={isLarge ? AvatarSize.ExtraLarge : AvatarSize.Large} />
					</div>

					<StackVertical gap={FlexGap.ExtraSmall} justify={FlexJustify.Center} extraClass='ml-sm'>
						<h4 className='m-0'>{data?.name}</h4>

						<StackHorizontal gap={FlexGap.ExtraSmall} wrap>
							{!data?.isVerified && <Pill colour={Colour.Alert} content={t('labels.Unverified')} />}
							<Pill content={`${t('labels.clientIdNo')}: ${data?.clientIdNo}`} />
							<Pill content={`${t('labels.plan')}: ${data?.planName}`} />
							{data?.isBusiness && <Pill content={t('labels.businessAccount')} />}
						</StackHorizontal>
					</StackVertical>

					<StackHorizontal
						align={FlexAlign.Center}
						justify={FlexJustify.Start}
						gap={FlexGap.Small}
						extraClass='ml-a'
					>
						{(isLarge || isXL || isXXL || isUltraWide) && (
							<Button
								title={t('actions.topUp')}
								colour={data?.balance <= 0 ? ColourStateSolid.Alert : ColourStateSolid.Secondary}
								actionLink={{
									url: '/manage-account/payment/top-up',
									title: `${t('actions.topUp')} • ${FormatCurrency(data?.balance)}`,
								}}
							/>
						)}
						<Tooltip
							content={t('actions.manageAccount')}
							disabled={isLarge || isXL || isXXL || isUltraWide}
						>
							<UserMenuDropdownMenu
								customTrigger={
									<Button
										title={t('actions.manageAccount')}
										icon={isTiny || isSmall || isMedium ? faCog : undefined}
										iconOnly={isTiny || isSmall || isMedium}
										showArrow
									/>
								}
							/>
						</Tooltip>
					</StackHorizontal>
				</div>
			)}
		</>
	);
}

export { AccountHeader };
